import {
  KnownHistoricalUtilisationTurnoverMetadataGroupBy,
  KnownHistoricalUtilisationPeakBandsMetadataGroupBy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useQuery } from 'react-query';
import { useApi } from '../../../core/api/api.context';
import { QueryKeys } from '../../../core/queries/query-keys.constants';

interface DeliveryContainerUtilisationQuery {
  groupBy: KnownHistoricalUtilisationTurnoverMetadataGroupBy;
  fromDate?: Date;
  toDate: Date;
  hubID?: string;
  /**
   * IANA time zone used to align daily report buckets server-side
   * (e.g. `Australia/Melbourne`). Omitting it falls back to the
   * backend default (`Australia/Melbourne`). Included in the query
   * key so a project-tz change refetches.
   */
  timeZone?: string;
}

export function useQueryDeliveryContainerUtilisation(
  projectID: string,
  { groupBy, fromDate, toDate, hubID, timeZone }: DeliveryContainerUtilisationQuery
) {
  const api = useApi();

  return useQuery(
    [
      QueryKeys.REPORTS,
      QueryKeys.DELIVERY_CONTAINER_UTILISATION,
      projectID,
      groupBy,
      fromDate,
      toDate,
      hubID,
      timeZone
    ],
    async () => {
      return await api.reportsController.turnover(projectID, groupBy, toDate, {
        fromDate,
        hubID,
        timeZone,
        includeChildProjects: true
      });
    }
  );
}

export function useQueryDeliveryContainerCapacity(
  projectID: string,
  { groupBy, fromDate, toDate, hubID, timeZone }: DeliveryContainerUtilisationQuery
) {
  const api = useApi();

  // The capacity SDK enum is structurally identical to the utilisation enum (Hub | Size | None).
  // Cast at the boundary so the hook can carry a single state type.
  const capacityGroupBy = groupBy as unknown as KnownHistoricalUtilisationPeakBandsMetadataGroupBy;

  return useQuery(
    [QueryKeys.REPORTS, QueryKeys.DELIVERY_CONTAINER_CAPACITY, projectID, groupBy, fromDate, toDate, hubID, timeZone],
    async () => {
      return await api.reportsController.peakBands(projectID, capacityGroupBy, toDate, {
        fromDate,
        hubID,
        timeZone,
        includeChildProjects: true
      });
    }
  );
}
